import React, { useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import {
	ArrowRight,
	Cart,
	DownLoadDiasbled,
	EditStudio,
	Print,
	Sizes,
	Studio,
} from '@/components/icon/IconList'
import Icon from '@/components/icon/Icon'
import Link from 'next/link'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { AllowanceList } from '@/components/Product/AllowanceChip'
import { TooltipWithIcon } from '@/components/Product/Handout/TooltipWithIcon'

export interface ChipProps {
	chipItem: AllowanceList
	handleDrawer: (chip: AllowanceList) => void
	productType: string
}

type ChipColorType = 'warning' | 'info' | 'studio'

export const enum AllowanceListType {
	print = 'print',
	viewer = 'viewer',
	editStudio = 'editStudio', //편집스튜디오
	writeStudio = 'writeStudio', //저작 스튜디오
	selling = 'slot',
	warningPrint = 'warningPrint',
}

export const HandoutAllowanceListTypeWithString = {
	[AllowanceListType.print]: '프린터 출력 (PC,100부)',
	[AllowanceListType.viewer]: '파일 다운로드 불가',
	[AllowanceListType.editStudio]: '자료 편집하기',
	[AllowanceListType.writeStudio]: '문제 편집 가능',
	[AllowanceListType.selling]: '자료 저작 및 판매 가능',
	[AllowanceListType.warningPrint]: '프린트 출력 불가',
}

export const handoutAllowanceChipList: AllowanceList[] = [
	{
		key: AllowanceListType.viewer,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/5',
		description:
			'쏠북의 모든 수업자료는 저작권 보호를 위해 쏠북의 뷰어 기능을 통해서만 열람할 수 있습니다. 프린트 출력은 뷰어의 출력하기 버튼을 통해 가능합니다. (PC에서만 가능)',
	},
	{
		key: AllowanceListType.print,
		color: 'info',
		hasInfo: false,
	},
	{
		key: AllowanceListType.editStudio,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/studio/edit',
		linkTitle: '편집하기 기능 알아보기',
		description: '문제의 순서를 바꾸거나 삭제할 수 있어요. ',
	},
	{
		key: AllowanceListType.writeStudio,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/6',
		linkTitle: '편집하기 기능 알아보기',
		description: '문제의 순서를 바꾸거나 삭제할 수 있어요. 또한 텍스트 수정도 가능해요.',
	},
]

export const HandoutAllowanceChip: React.FC<ChipProps> = ({
	chipItem,
	handleDrawer,
	productType,
}) => {
	const theme = useTheme()
	const { isTablet } = useWindowDimensions()
	const { key: allowanceKey, color, link, hasInfo, description, linkTitle } = chipItem
	const title = HandoutAllowanceListTypeWithString[allowanceKey]
	return (
		<AllowanceChipWrapper
			type={color}
			style={{ pointerEvents: hasInfo ? 'auto' : 'none' }}
			onMouseEnter={() => {
				if (isTablet) return
				mixpanelEvent('Product Detail Page Marker hovered', {
					'Product Type': productType,
					'Marker Name': title,
					'Marker Btn Link': link,
				})
			}}
			onClick={() => {
				if (isTablet) {
					handleDrawer(chipItem)
					mixpanelEvent('Product Detail Page Marker hovered', {
						'Product Type': productType,
						'Marker Name': title,
						'Marker Btn Link': link,
					})
				}
			}}>
			<TooltipWithIcon
				color={theme.new.colors.sol_gray_600}
				icon={null}
				message={
					<TooltipItem
						title={title}
						link={link}
						description={description}
						productType={productType}
						linkTitle={linkTitle}
					/>
				}
				link={link}
				size={Sizes.small}>
				<TooltipContent allowanceKey={allowanceKey} title={title} />
			</TooltipWithIcon>
		</AllowanceChipWrapper>
	)
}
const TooltipContent = ({
	allowanceKey,
	title,
}: {
	allowanceKey: AllowanceListType
	title: string
}) => {
	const renderIcon = useCallback(() => {
		switch (allowanceKey) {
			case AllowanceListType.print:
				return <Icon icon={Print} size={Sizes.medium} />
			case AllowanceListType.viewer:
				return <Icon icon={DownLoadDiasbled} size={Sizes.medium} />
			case AllowanceListType.editStudio:
				return <Icon icon={EditStudio} size={Sizes.medium} />
			case AllowanceListType.writeStudio:
				return <Icon icon={Studio} size={Sizes.medium} />
			case AllowanceListType.selling:
				return <Icon icon={Cart} size={Sizes.medium} />
			case AllowanceListType.warningPrint:
				return <Icon icon={Print} size={Sizes.medium} />
			default:
				return null
		}
	}, [])

	return (
		<TooltipContentLayout>
			<ChipLayoutContainer>{renderIcon()}</ChipLayoutContainer>
			<p>
				{title}
				{allowanceKey === AllowanceListType.editStudio && <Em>Beta</Em>}
			</p>
		</TooltipContentLayout>
	)
}
const TooltipContentLayout = styled.div`
	white-space: nowrap;

	> p {
		white-space: nowrap;
	}
`
export const TooltipItem = ({
	title,
	link,
	description,
	productType,
	linkTitle,
}: {
	title: string
	link: string
	description: string
	productType: string
	linkTitle: string
}) => {
	const theme = useTheme()
	return (
		<TooltipWrapper>
			<b className={'title'}>{title}</b>
			<div className={'description'}>{description}</div>
			{link && (
				<Link
					href={link}
					target={'_blank'}
					onClick={() =>
						mixpanelEvent('Product Detail Page Marker Detail Clicked', {
							'Product Type': productType,
							'Marker Name': title,
							'Marker Btn Link': link,
						})
					}>
					{linkTitle || '자세히 보기'}
					<Icon
						icon={ArrowRight}
						size={Sizes.xsmall}
						color={theme.new.colors.indigo500}
					/>
				</Link>
			)}
		</TooltipWrapper>
	)
}
const Em = styled.em`
	color: ${(props) => props.theme.new.colors.sol_mint_300};
	text-align: right;
	font-size: 10px;
	font-weight: 700;
	line-height: 18px;
	vertical-align: text-bottom;
	margin-left: 2px;
`
const ChipLayoutContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 8px;
`

const AllowanceChipWrapper = styled.div<{ type: ChipColorType }>`
	font-size: 14px;
	display: flex;
	//width: 100%;
	position: relative;
	align-items: center;
	flex-direction: column;
	line-height: 22px;
	border-radius: 4px;
	border: 1px solid ${(props) => props.theme.new.colors.sol_gray_100};
	padding: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
	letter-spacing: -0.14px;

	div {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
	}

	> p {
		margin: 0;
	}

	i {
		margin-left: 4px;
		margin-bottom: 2px;
	}

	${(props) =>
		props.type === 'warning' &&
		`
		div {
			color: ${props.theme.new.colors.sol_red_400};
		}
	
	border: 1px solid ${props.theme.new.colors.sol_red_100};
	`}
	.chip-desc {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
	}

	.tooltip-desc {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
		max-width: 410px;
	}

	:hover {
		border: 1px solid ${(props) => props.theme.new.colors.sol_gray_600};
		background-color: ${(props) => props.theme.new.colors.sol_gray_0};
	}
`
const TooltipWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;

	> b {
		text-align: left;
		font-size: 16px;
		font-weight: 600;
		color: ${(props) => props.theme.new.colors.sol_gray_800};
	}

	div {
		display: flex;
		font-size: 14px;
		color: ${(props) => props.theme.new.colors.sol_gray_600};
		width: 380px;
		word-wrap: break-word;
	}

	a {
		color: ${(props) => props.theme.new.colors.indigo500};
		display: flex;
		font-size: 14px;
		align-items: center;
		font-weight: 600;
	}

	.title {
		font-size: 20px;
		font-weight: 700;
	}

	.description {
		font-size: 16px;
		font-weight: 400;
		line-height: 1.5;
		letter-spacing: -0.16px;
		margin-bottom: 8px;
	}
`
